import React from 'react';
import {
    TrendingUp,
    TrendingDown,
    Minus,
    DollarSign,
    Users,
    Image,
    Pause,
    Play,
    ExternalLink,
    Target,
    Lightbulb,
    Layers,
    CheckCircle,
    ArrowRight
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

/**
 * InlineDataCard - Renders contextual data cards inline in chat messages
 * Supports campaign, audience, creative, strategy, and recommendation card types
 */
const InlineDataCard = ({ type, data }) => {
    switch (type) {
        case 'campaign':
            return <CampaignCard data={data} />;
        case 'audience':
            return <AudienceCard data={data} />;
        case 'creative':
            return <CreativeCard data={data} />;
        case 'strategy':
            return <StrategyCard data={data} />;
        case 'recommendation':
            return <RecommendationCard data={data} />;
        default:
            return <GenericCard data={data} />;
    }
};

/**
 * CampaignCard - Displays campaign metrics inline
 */
const CampaignCard = ({ data }) => {
    const {
        name,
        status,
        spend,
        roas,
        dailyBudget,
        impressions,
        clicks,
        conversions,
        ctr,
        cpc
    } = data;

    const isActive = status?.toUpperCase() === 'ACTIVE';
    const roasColor = getRoasColor(roas);

    return (
        <div style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <StatusIndicator active={isActive} />
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)'
                    }}>
                        {name}
                    </span>
                </div>
                <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    textTransform: 'uppercase'
                }}>
                    {status}
                </span>
            </div>

            {/* Metrics grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 'var(--space-3)',
                marginTop: 'var(--space-3)'
            }}>
                <MetricCell
                    label="Spend"
                    value={formatCurrency(spend || 0)}
                    subValue={dailyBudget ? `$${dailyBudget}/day` : null}
                />
                <MetricCell
                    label="ROAS"
                    value={`${(roas || 0).toFixed(2)}x`}
                    valueColor={roasColor}
                />
                <MetricCell
                    label="Conversions"
                    value={conversions || 0}
                />
            </div>

            {/* Secondary metrics */}
            {(impressions || clicks || ctr) && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-4)',
                    marginTop: 'var(--space-3)',
                    paddingTop: 'var(--space-2)',
                    borderTop: '1px solid var(--color-border-light)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    {impressions !== undefined && (
                        <span>{formatNumber(impressions)} impressions</span>
                    )}
                    {clicks !== undefined && (
                        <span>{formatNumber(clicks)} clicks</span>
                    )}
                    {ctr !== undefined && (
                        <span>{(ctr * 100).toFixed(2)}% CTR</span>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * AudienceCard - Displays audience information inline
 */
const AudienceCard = ({ data }) => {
    const {
        name,
        size,
        performance,
        spend,
        conversions,
        cpa
    } = data;

    return (
        <div style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)'
            }}>
                <Users size={16} style={{ color: 'var(--color-primary)' }} />
                <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)'
                }}>
                    {name}
                </span>
                {performance && (
                    <PerformanceBadge performance={performance} />
                )}
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 'var(--space-3)',
                marginTop: 'var(--space-2)'
            }}>
                {size && (
                    <MetricCell label="Size" value={formatNumber(size)} />
                )}
                {spend !== undefined && (
                    <MetricCell label="Spend" value={formatCurrency(spend)} />
                )}
                {cpa !== undefined && (
                    <MetricCell label="CPA" value={formatCurrency(cpa)} />
                )}
            </div>
        </div>
    );
};

/**
 * CreativeCard - Displays creative information inline
 */
const CreativeCard = ({ data }) => {
    const {
        name,
        thumbnail,
        type: creativeType,
        impressions,
        clicks,
        ctr,
        spend,
        performance
    } = data;

    return (
        <div style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            display: 'flex',
            gap: 'var(--space-3)'
        }}>
            {/* Thumbnail */}
            {thumbnail ? (
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: 'var(--color-surface)'
                }}>
                    <img
                        src={thumbnail}
                        alt={name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                </div>
            ) : (
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Image size={24} style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-1)'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {name}
                    </span>
                    {performance && (
                        <PerformanceBadge performance={performance} />
                    )}
                </div>

                {creativeType && (
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)',
                        textTransform: 'capitalize'
                    }}>
                        {creativeType}
                    </span>
                )}

                <div style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    marginTop: 'var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)'
                }}>
                    {ctr !== undefined && (
                        <span>{(ctr * 100).toFixed(2)}% CTR</span>
                    )}
                    {clicks !== undefined && (
                        <span>{formatNumber(clicks)} clicks</span>
                    )}
                    {spend !== undefined && (
                        <span>{formatCurrency(spend)}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * StrategyCard - Displays funnel strategy with stages
 */
const StrategyCard = ({ data }) => {
    const { title, sections = [] } = data;

    const stageColors = {
        'TOFU': { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', color: '#3B82F6' },
        'MOFU': { bg: 'rgba(168, 85, 247, 0.1)', border: 'rgba(168, 85, 247, 0.3)', color: '#A855F7' },
        'BOFU': { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', color: '#22C55E' }
    };

    const getStageStyle = (stage) => {
        const stageKey = stage?.toUpperCase().includes('TOFU') ? 'TOFU' :
                         stage?.toUpperCase().includes('MOFU') ? 'MOFU' :
                         stage?.toUpperCase().includes('BOFU') ? 'BOFU' : 'TOFU';
        return stageColors[stageKey];
    };

    return (
        <div style={{
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05), rgba(59, 130, 246, 0.05))',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'rgba(124, 58, 237, 0.1)',
                borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
            }}>
                <Layers size={16} style={{ color: 'var(--color-primary)' }} />
                <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)'
                }}>
                    {title || 'Funnel Strategy'}
                </span>
            </div>

            {/* Stages */}
            <div style={{ padding: 'var(--space-3)' }}>
                {sections.map((section, index) => {
                    const stageStyle = getStageStyle(section.stage);
                    return (
                        <div key={index} style={{
                            marginBottom: index < sections.length - 1 ? 'var(--space-3)' : 0
                        }}>
                            {/* Stage header */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                <div style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: 'var(--radius-full)',
                                    background: stageStyle.bg,
                                    border: `1px solid ${stageStyle.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 700,
                                    color: stageStyle.color
                                }}>
                                    {index + 1}
                                </div>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    color: stageStyle.color
                                }}>
                                    {section.stage}
                                </span>
                                {section.budget && (
                                    <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)',
                                        marginLeft: 'auto'
                                    }}>
                                        {section.budget} of budget
                                    </span>
                                )}
                            </div>

                            {/* Stage content */}
                            <div style={{
                                marginLeft: '32px',
                                padding: 'var(--space-2) var(--space-3)',
                                background: stageStyle.bg,
                                borderRadius: 'var(--radius-md)',
                                borderLeft: `3px solid ${stageStyle.color}`
                            }}>
                                {section.objective && (
                                    <div style={{ marginBottom: 'var(--space-2)' }}>
                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Objective: </span>
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{section.objective}</span>
                                    </div>
                                )}
                                {section.audiences && section.audiences.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-2)' }}>
                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Audiences: </span>
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                            {section.audiences.join(', ')}
                                        </span>
                                    </div>
                                )}
                                {section.creatives && section.creatives.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-2)' }}>
                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Creatives: </span>
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                            {section.creatives.join(', ')}
                                        </span>
                                    </div>
                                )}
                                {section.kpis && section.kpis.length > 0 && (
                                    <div>
                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Target KPIs: </span>
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                            {section.kpis.join(', ')}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Arrow between stages */}
                            {index < sections.length - 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: 'var(--space-2) 0'
                                }}>
                                    <ArrowRight size={16} style={{ color: 'var(--color-text-tertiary)', transform: 'rotate(90deg)' }} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * RecommendationCard - Displays a strategic recommendation
 */
const RecommendationCard = ({ data }) => {
    const { title, priority, description, steps = [], expectedOutcome } = data;

    const priorityConfig = {
        high: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', color: 'var(--color-error)', label: 'High Priority' },
        medium: { bg: 'rgba(234, 179, 8, 0.1)', border: 'rgba(234, 179, 8, 0.3)', color: 'var(--color-warning)', label: 'Medium Priority' },
        low: { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', color: 'var(--color-success)', label: 'Suggestion' }
    };

    const config = priorityConfig[priority] || priorityConfig.medium;

    return (
        <div style={{
            background: 'var(--color-surface-elevated)',
            border: `1px solid ${config.border}`,
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-3) var(--space-4)',
                background: config.bg,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
            }}>
                <Lightbulb size={16} style={{ color: config.color }} />
                <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    flex: 1
                }}>
                    {title || 'Recommendation'}
                </span>
                <span style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 500,
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    background: config.bg,
                    color: config.color,
                    border: `1px solid ${config.border}`
                }}>
                    {config.label}
                </span>
            </div>

            {/* Content */}
            <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
                {description && (
                    <p style={{
                        margin: '0 0 var(--space-3) 0',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 1.5
                    }}>
                        {description}
                    </p>
                )}

                {steps.length > 0 && (
                    <div style={{ marginBottom: 'var(--space-3)' }}>
                        <span style={{
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 600,
                            color: 'var(--color-text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Steps
                        </span>
                        <ul style={{
                            margin: 'var(--space-2) 0 0 0',
                            paddingLeft: 'var(--space-4)',
                            listStyle: 'none'
                        }}>
                            {steps.map((step, index) => (
                                <li key={index} style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-2)',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-primary)'
                                }}>
                                    <CheckCircle size={14} style={{
                                        color: 'var(--color-success)',
                                        marginTop: '2px',
                                        flexShrink: 0
                                    }} />
                                    {step}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {expectedOutcome && (
                    <div style={{
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'rgba(34, 197, 94, 0.1)',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <Target size={14} style={{ color: 'var(--color-success)' }} />
                        <span style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            <strong>Expected Outcome:</strong> {expectedOutcome}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * GenericCard - Fallback for unknown card types
 */
const GenericCard = ({ data }) => {
    return (
        <div style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
        }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(data, null, 2)}
            </pre>
        </div>
    );
};

/**
 * Helper components
 */
const StatusIndicator = ({ active }) => (
    <div style={{
        width: '8px',
        height: '8px',
        borderRadius: 'var(--radius-full)',
        background: active ? 'var(--color-success)' : 'var(--color-text-tertiary)'
    }} />
);

const MetricCell = ({ label, value, subValue, valueColor }) => (
    <div>
        <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)',
            marginBottom: '2px'
        }}>
            {label}
        </div>
        <div style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: valueColor || 'var(--color-text-primary)'
        }}>
            {value}
        </div>
        {subValue && (
            <div style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)'
            }}>
                {subValue}
            </div>
        )}
    </div>
);

const PerformanceBadge = ({ performance }) => {
    const config = {
        excellent: { bg: 'rgba(34, 197, 94, 0.1)', color: 'var(--color-success)', label: 'Top' },
        good: { bg: 'rgba(59, 130, 246, 0.1)', color: 'var(--color-info)', label: 'Good' },
        average: { bg: 'rgba(234, 179, 8, 0.1)', color: 'var(--color-warning)', label: 'Avg' },
        poor: { bg: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', label: 'Low' }
    };

    const { bg, color, label } = config[performance] || config.average;

    return (
        <span style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            padding: '2px 6px',
            borderRadius: 'var(--radius-sm)',
            background: bg,
            color: color
        }}>
            {label}
        </span>
    );
};

/**
 * Helper functions
 */
const getRoasColor = (roas) => {
    if (!roas) return 'var(--color-text-primary)';
    if (roas >= 3) return 'var(--color-success)';
    if (roas >= 2) return 'var(--color-info)';
    if (roas >= 1) return 'var(--color-warning)';
    return 'var(--color-error)';
};

const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
};

export default InlineDataCard;
